import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import { TimeSeriesService, useScatterplotContext } from 'features/timeSeries'
import {
  resolveSingleScaleYAxisRange,
  resolveYAxisRange,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { estimateSampleIntervalMs } from 'features/timeSeries/utils/sampleCadence'
import {
  clampTimeRange,
  minSpanForSampleIntervalMs,
} from 'features/timeSeries/utils/timeRangeClamp'
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import uPlot from 'uplot'

import { getPlotBounds } from './mdDraftAlertCaseFlowUtils'
import {
  expandAxisRangeUpdates,
  NumericAxisRange,
  translateAxisRange,
} from './viewportInteractionUtils'
import {
  SINGLE_SCALE_AXIS_ID,
  VisibleSensorToRender,
  YAxisOverlayLabel,
} from './yAxisOverlayLabels'

interface BoxZoomRect {
  left: number
  top: number
  width: number
  height: number
}

interface LabelDragSession {
  axisIds: string[]
  didMove: boolean
  lastRanges: Record<string, NumericAxisRange>
  startRanges: Record<string, NumericAxisRange>
  startClientY: number
}

interface BoxZoomSession {
  currentX: number
  currentY: number
  didMove: boolean
  startX: number
  startY: number
}

interface AxisResetTarget {
  mode: 'auto' | 'custom'
  range?: NumericAxisRange
}

interface UseChartViewportInteractionsArgs {
  enabled: boolean
  autoScaleMode: boolean
  customAxisRanges: Record<string, { min?: number; max?: number }>
  setAutoScaleMode: Dispatch<SetStateAction<boolean>>
  setCustomAxisRanges: Dispatch<
    SetStateAction<Record<string, { min?: number; max?: number }>>
  >
  setCustomXAxisRange: Dispatch<SetStateAction<{ min?: number; max?: number }>>
  setDateMode: Dispatch<SetStateAction<'absolute' | 'relative'>>
  setLiveTail: Dispatch<SetStateAction<boolean>>
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  chartData: Array<Record<string, unknown>>
  loadTriggerRef: MutableRefObject<'zoom' | 'other'>
  singleScaleMode: boolean
  allSensorsToRender: Array<{ id: string; name: string }>
  timeRange: TimeRange
  visibleSensorsToRender: VisibleSensorToRender[]
  sensorRanges: Record<string, { min?: number; max?: number; unit: string }>
  chartAreaRef: MutableRefObject<HTMLDivElement | null>
  uPlotRef: MutableRefObject<uPlot | null>
  uPlotRecreateKey: string
  hasDataPoints: boolean
  xAxisSensorId: string | null
}

const DRAG_THRESHOLD_PX = 4
const MIN_VISIBLE_OVERLAP_RATIO = 0.08

const suppressNextWindowClick = () => {
  const cleanup = () => {
    window.removeEventListener('click', handleCaptureClick, true)
  }
  const handleCaptureClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    cleanup()
  }

  window.addEventListener('click', handleCaptureClick, true)
  // Drop the listener on the next frame even if no click fires (e.g. mousedown
  // and mouseup targets lack a common ancestor, as when release lands in app
  // chrome). Otherwise the handler leaks and eats a later legitimate click.
  requestAnimationFrame(cleanup)
}
const cloneNumericRangeMap = (
  ranges: Record<string, NumericAxisRange>
): Record<string, { min?: number; max?: number }> =>
  Object.fromEntries(
    Object.entries(ranges).map(([axisId, range]) => [
      axisId,
      { min: range.min, max: range.max },
    ])
  )

export const useChartViewportInteractions = ({
  enabled,
  autoScaleMode,
  customAxisRanges,
  setAutoScaleMode,
  setCustomAxisRanges,
  setCustomXAxisRange,
  setDateMode,
  setLiveTail,
  setTimeRange,
  chartData,
  loadTriggerRef,
  singleScaleMode,
  allSensorsToRender,
  timeRange,
  visibleSensorsToRender,
  sensorRanges,
  chartAreaRef,
  uPlotRef,
  uPlotRecreateKey,
  hasDataPoints,
  xAxisSensorId,
}: UseChartViewportInteractionsArgs) => {
  // Pen count for the box-zoom interval computation, mirrored into a ref so
  // the window mousemove/mouseup effect below does not resubscribe (and
  // cannot go stale mid-drag) when pens change. The interval baked into the
  // zoomed range shares the bucket budget across pens and must match what
  // buildTimeSeriesRequest would compute for the same range.
  const { selectedSensors } = useScatterplotContext()
  const penCountRef = useRef(selectedSensors.length)
  useEffect(() => {
    penCountRef.current = selectedSensors.length
  }, [selectedSensors.length])

  const [hoveredAxisId, setHoveredAxisId] = useState<string | null>(null)
  const [selectedAxisIds, setSelectedAxisIds] = useState<string[]>([])
  const [previewAxisRanges, setPreviewAxisRanges] = useState<
    Record<string, { min?: number; max?: number }>
  >({})
  const [previewXAxisRange, setPreviewXAxisRange] = useState<{
    max: number
    min: number
  } | null>(null)
  const [boxZoomRect, setBoxZoomRect] = useState<BoxZoomRect | null>(null)
  const [isLabelDragging, setIsLabelDragging] = useState(false)
  const [isBoxZoomDragging, setIsBoxZoomDragging] = useState(false)
  const labelDragSessionRef = useRef<LabelDragSession | null>(null)
  const boxZoomSessionRef = useRef<BoxZoomSession | null>(null)
  const pendingBoxZoomAxisRangesRef = useRef<Record<
    string,
    NumericAxisRange
  > | null>(null)
  const axisResetTargetsRef = useRef<Record<string, AxisResetTarget>>({})
  const timeRangeFrom = timeRange.from
  const timeRangeTo = timeRange.to
  const chartDataXExtent = useMemo(() => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY

    chartData.forEach((point) => {
      const timestamp = point.timestamp
      if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
        return
      }

      min = Math.min(min, timestamp)
      max = Math.max(max, timestamp)
    })

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null
    }

    return { min, max }
  }, [chartData])

  // How far this chart's data can honestly be zoomed. Kept in a ref because the
  // box-zoom mouseup handler is attached once per drag and must not close over a
  // stale estimate if data lands mid-gesture.
  const minZoomSpanRef = useRef(minSpanForSampleIntervalMs())
  useEffect(() => {
    const timestamps = chartData
      .map((point) => point.timestamp)
      .filter((timestamp): timestamp is number => typeof timestamp === 'number')

    minZoomSpanRef.current = minSpanForSampleIntervalMs(
      estimateSampleIntervalMs(timestamps)
    )
  }, [chartData])

  const allAxisIds = useMemo(
    () => allSensorsToRender.map(({ id }) => id),
    [allSensorsToRender]
  )
  const boxZoomAxisIds = useMemo(
    () =>
      singleScaleMode
        ? [SINGLE_SCALE_AXIS_ID]
        : Array.from(new Set(visibleSensorsToRender.map(({ id }) => id))),
    [singleScaleMode, visibleSensorsToRender]
  )
  const sensorDataExtents = useMemo(() => {
    const extents: Record<string, { min: number; max: number }> = {}

    visibleSensorsToRender.forEach(({ id }) => {
      let min = Number.POSITIVE_INFINITY
      let max = Number.NEGATIVE_INFINITY

      chartData.forEach((point) => {
        const value = point[id]
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return
        }

        min = Math.min(min, value)
        max = Math.max(max, value)
      })

      if (Number.isFinite(min) && Number.isFinite(max)) {
        extents[id] = { min, max }
      }
    })

    return extents
  }, [chartData, visibleSensorsToRender])
  const singleScaleDataRange = useMemo(() => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY

    visibleSensorsToRender.forEach(({ id }) => {
      const sensorRange = sensorDataExtents[id]
      if (!sensorRange) {
        return
      }

      if (Number.isFinite(sensorRange.min)) {
        min = Math.min(min, sensorRange.min)
      }
      if (Number.isFinite(sensorRange.max)) {
        max = Math.max(max, sensorRange.max)
      }
    })

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null
    }

    return { min, max }
  }, [sensorDataExtents, visibleSensorsToRender])

  const clampTranslatedRange = useCallback(
    (
      axisId: string,
      nextRange: NumericAxisRange,
      startRange: NumericAxisRange
    ) => {
      const dataRange =
        axisId === SINGLE_SCALE_AXIS_ID
          ? singleScaleDataRange
          : sensorDataExtents[axisId]

      if (
        !dataRange ||
        !Number.isFinite(dataRange.min) ||
        !Number.isFinite(dataRange.max)
      ) {
        return nextRange
      }

      const windowSpan = Math.abs(startRange.max - startRange.min)
      const dataSpan = Math.abs(dataRange.max - dataRange.min)
      const overlapMargin = Math.min(
        windowSpan * MIN_VISIBLE_OVERLAP_RATIO,
        dataSpan / 2
      )

      const minimumDelta = dataRange.min - startRange.max
      const maximumDelta = dataRange.max - startRange.min
      const minimumVisibleDelta = dataRange.min + overlapMargin - startRange.max
      const maximumVisibleDelta = dataRange.max - overlapMargin - startRange.min
      const attemptedDelta = nextRange.min - startRange.min
      const clampedDelta = Math.min(
        Math.min(maximumDelta, maximumVisibleDelta),
        Math.max(minimumDelta, minimumVisibleDelta, attemptedDelta)
      )

      return translateAxisRange(startRange, clampedDelta)
    },
    [sensorDataExtents, singleScaleDataRange]
  )
  const getDefaultAutoAxisRange = useCallback(
    (axisId: string): NumericAxisRange => {
      if (axisId === SINGLE_SCALE_AXIS_ID) {
        return resolveSingleScaleYAxisRange(
          allAxisIds.map((id) => ({ id })),
          sensorRanges
        )
      }

      return resolveYAxisRange(sensorRanges[axisId])
    },
    [allAxisIds, sensorRanges]
  )
  const captureAxisResetTargets = useCallback(
    (axisIds: string[], uPlotInstance: uPlot) => {
      axisIds.forEach((axisId) => {
        if (axisResetTargetsRef.current[axisId]) {
          return
        }

        const customRangeKey =
          axisId === SINGLE_SCALE_AXIS_ID ? SINGLE_SCALE_AXIS_ID : axisId
        const customRange = customAxisRanges[customRangeKey]
        const hasCustomRange =
          customRange &&
          customRange.min !== undefined &&
          customRange.max !== undefined

        if (!hasCustomRange) {
          axisResetTargetsRef.current[axisId] = { mode: 'auto' }
          return
        }

        const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
        const scale = uPlotInstance.scales[scaleKey]
        if (
          !scale ||
          typeof scale.min !== 'number' ||
          typeof scale.max !== 'number'
        ) {
          axisResetTargetsRef.current[axisId] = { mode: 'auto' }
          return
        }

        axisResetTargetsRef.current[axisId] = {
          mode: 'custom',
          range: {
            min: scale.min,
            max: scale.max,
          },
        }
      })
    },
    [customAxisRanges]
  )
  const visibleAxisIdSet = useMemo(
    () =>
      new Set(
        singleScaleMode
          ? [SINGLE_SCALE_AXIS_ID]
          : visibleSensorsToRender.map(({ id }) => id)
      ),
    [singleScaleMode, visibleSensorsToRender]
  )

  useEffect(() => {
    setSelectedAxisIds((previousAxisIds) =>
      previousAxisIds.filter((axisId) => visibleAxisIdSet.has(axisId))
    )
  }, [visibleAxisIdSet])

  useEffect(() => {
    if (enabled) {
      return
    }

    labelDragSessionRef.current = null
    boxZoomSessionRef.current = null
    setIsLabelDragging(false)
    setIsBoxZoomDragging(false)
    setHoveredAxisId(null)
    setPreviewAxisRanges({})
    setPreviewXAxisRange(null)
    pendingBoxZoomAxisRangesRef.current = null
    axisResetTargetsRef.current = {}
    setBoxZoomRect(null)
    setSelectedAxisIds([])
  }, [enabled])

  const applyAxisRangePreview = useCallback(
    (
      uPlotInstance: uPlot,
      rangeUpdates: Record<string, NumericAxisRange>
    ): void => {
      Object.entries(rangeUpdates).forEach(([axisId, range]) => {
        const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
        uPlotInstance.setScale(scaleKey, range)
      })
    },
    []
  )

  const commitAxisRanges = useCallback(
    (rangeUpdates: Record<string, NumericAxisRange>) => {
      if (Object.keys(rangeUpdates).length === 0) {
        return
      }

      const expandedRangeUpdates = expandAxisRangeUpdates({
        rangeUpdates,
        singleScaleMode,
        allAxisIds,
      })

      if (Object.keys(expandedRangeUpdates).length === 0) {
        return
      }

      // First-drag commit: the y-axis default in generateUPlotScales is
      // `[0, max + buffer]`, which is inverted for sensors whose data is
      // all-negative. The live uPlot instance survived that on first mount,
      // but when the commit triggers a recreate the new instance renders
      // blank lines for every non-dragged axis on an inverted default range.
      // Snapshot the live scale min/max for every axis first so the recreate
      // starts from ranges that were known to render correctly.
      const baselineRanges: Record<string, { min?: number; max?: number }> = {}
      if (autoScaleMode) {
        const uPlotInstance = uPlotRef.current
        if (uPlotInstance) {
          allAxisIds.forEach((axisId) => {
            const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
            const scale = uPlotInstance.scales[scaleKey]
            if (
              scale &&
              typeof scale.min === 'number' &&
              typeof scale.max === 'number'
            ) {
              baselineRanges[axisId] = { min: scale.min, max: scale.max }
            }
          })
        }
        setAutoScaleMode(false)
      }

      setCustomAxisRanges((previousRanges) => ({
        ...previousRanges,
        ...baselineRanges,
        ...expandedRangeUpdates,
      }))
    },
    [
      allAxisIds,
      autoScaleMode,
      setCustomAxisRanges,
      setAutoScaleMode,
      singleScaleMode,
      uPlotRef,
    ]
  )

  useEffect(() => {
    if (xAxisSensorId || !previewXAxisRange) {
      return
    }

    const committedMin = Date.parse(timeRangeFrom)
    const committedMax = Date.parse(timeRangeTo)
    const timeRangeMatchesPreview =
      Number.isFinite(committedMin) &&
      Number.isFinite(committedMax) &&
      Math.abs(committedMin - previewXAxisRange.min) <= 1 &&
      Math.abs(committedMax - previewXAxisRange.max) <= 1

    if (!timeRangeMatchesPreview) {
      pendingBoxZoomAxisRangesRef.current = null
      setPreviewAxisRanges({})
      setPreviewXAxisRange(null)
      return
    }

    if (
      chartDataXExtent &&
      chartDataXExtent.min >= previewXAxisRange.min - 1000 &&
      chartDataXExtent.max <= previewXAxisRange.max + 1000
    ) {
      if (pendingBoxZoomAxisRangesRef.current) {
        commitAxisRanges(pendingBoxZoomAxisRangesRef.current)
        pendingBoxZoomAxisRangesRef.current = null
      }
      setPreviewAxisRanges({})
      setPreviewXAxisRange(null)
    }
  }, [
    chartDataXExtent,
    commitAxisRanges,
    previewXAxisRange,
    timeRangeFrom,
    timeRangeTo,
    xAxisSensorId,
  ])

  const handleAxisLabelMouseDown = useCallback(
    (
      label: Pick<YAxisOverlayLabel, 'axisId'>,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      if (!enabled || !hasDataPoints || event.button !== 0) {
        return
      }

      const nextAxisIds = event.shiftKey
        ? Array.from(new Set([...selectedAxisIds, label.axisId]))
        : [label.axisId]

      event.preventDefault()
      event.stopPropagation()

      const uPlotInstance = uPlotRef.current
      if (!uPlotInstance) {
        return
      }

      const startRanges = nextAxisIds.reduce<Record<string, NumericAxisRange>>(
        (ranges, axisId) => {
          const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
          const scale = uPlotInstance.scales[scaleKey]
          const min = scale?.min
          const max = scale?.max
          if (
            min === null ||
            min === undefined ||
            max === null ||
            max === undefined
          ) {
            return ranges
          }

          ranges[axisId] = { min, max }
          return ranges
        },
        {}
      )

      if (Object.keys(startRanges).length === 0) {
        return
      }

      setSelectedAxisIds(nextAxisIds)
      setHoveredAxisId(label.axisId)
      setBoxZoomRect(null)
      setPreviewAxisRanges({})
      captureAxisResetTargets(nextAxisIds, uPlotInstance)

      labelDragSessionRef.current = {
        axisIds: nextAxisIds,
        didMove: false,
        lastRanges: {},
        startRanges,
        startClientY: event.clientY,
      }
      setIsLabelDragging(true)
    },
    [captureAxisResetTargets, enabled, hasDataPoints, selectedAxisIds, uPlotRef]
  )
  const handleAxisLabelDoubleClick = useCallback(
    (
      label: Pick<YAxisOverlayLabel, 'axisId'>,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      if (!enabled || !hasDataPoints) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const uPlotInstance = uPlotRef.current
      if (!uPlotInstance) {
        return
      }

      const targetAxisIds =
        selectedAxisIds.length > 1 && selectedAxisIds.includes(label.axisId)
          ? selectedAxisIds
          : [label.axisId]

      captureAxisResetTargets(targetAxisIds, uPlotInstance)

      const nextPreviewRanges = targetAxisIds.reduce<
        Record<string, NumericAxisRange>
      >((ranges, axisId) => {
        const resetTarget = axisResetTargetsRef.current[axisId]
        ranges[axisId] =
          resetTarget?.mode === 'custom' && resetTarget.range
            ? resetTarget.range
            : getDefaultAutoAxisRange(axisId)
        return ranges
      }, {})

      applyAxisRangePreview(uPlotInstance, nextPreviewRanges)
      setPreviewAxisRanges(cloneNumericRangeMap(nextPreviewRanges))
      setHoveredAxisId(null)
      setSelectedAxisIds([])

      setCustomAxisRanges((previousRanges) => {
        const nextRanges = { ...previousRanges }

        targetAxisIds.forEach((axisId) => {
          const resetTarget = axisResetTargetsRef.current[axisId]
          const targetKey =
            axisId === SINGLE_SCALE_AXIS_ID ? SINGLE_SCALE_AXIS_ID : axisId

          if (resetTarget?.mode === 'custom' && resetTarget.range) {
            nextRanges[targetKey] = {
              min: resetTarget.range.min,
              max: resetTarget.range.max,
            }
            return
          }

          delete nextRanges[targetKey]

          if (axisId === SINGLE_SCALE_AXIS_ID) {
            allAxisIds.forEach((sensorId) => {
              delete nextRanges[sensorId]
            })
          }
        })

        return nextRanges
      })
    },
    [
      allAxisIds,
      applyAxisRangePreview,
      captureAxisResetTargets,
      enabled,
      getDefaultAutoAxisRange,
      hasDataPoints,
      selectedAxisIds,
      setCustomAxisRanges,
      uPlotRef,
    ]
  )

  useEffect(() => {
    if (!isLabelDragging) {
      return
    }

    const handleWindowMouseMove = (event: MouseEvent) => {
      const session = labelDragSessionRef.current
      const uPlotInstance = uPlotRef.current
      if (!session || !uPlotInstance) {
        return
      }

      const overRect = uPlotInstance.over.getBoundingClientRect()
      const nextRanges: Record<string, NumericAxisRange> = {}
      const hasExceededDragThreshold =
        session.didMove ||
        Math.abs(event.clientY - session.startClientY) >= DRAG_THRESHOLD_PX

      if (!hasExceededDragThreshold) {
        return
      }

      session.didMove = true

      session.axisIds.forEach((axisId) => {
        const startRange = session.startRanges[axisId]
        if (!startRange || overRect.height <= 0) {
          return
        }

        const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
        const span = startRange.max - startRange.min
        const clampedStartClientY = Math.min(
          overRect.bottom,
          Math.max(overRect.top, session.startClientY)
        )
        const clampedCurrentClientY = Math.min(
          overRect.bottom,
          Math.max(overRect.top, event.clientY)
        )
        const deltaValue =
          ((clampedCurrentClientY - clampedStartClientY) / overRect.height) *
          span

        const nextRange = clampTranslatedRange(
          axisId,
          translateAxisRange(startRange, deltaValue),
          startRange
        )

        nextRanges[axisId] = nextRange
        uPlotInstance.setScale(scaleKey, nextRange)
      })

      session.lastRanges = nextRanges
      setPreviewAxisRanges(cloneNumericRangeMap(nextRanges))
    }

    const handleWindowMouseUp = () => {
      const session = labelDragSessionRef.current
      if (session?.didMove) {
        suppressNextWindowClick()
        commitAxisRanges(session.lastRanges)
      }

      labelDragSessionRef.current = null
      setHoveredAxisId(null)
      setIsLabelDragging(false)
      setPreviewAxisRanges({})
    }

    const handleWindowBlur = () => {
      handleWindowMouseUp()
    }

    window.addEventListener('mousemove', handleWindowMouseMove)
    window.addEventListener('mouseup', handleWindowMouseUp)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove)
      window.removeEventListener('mouseup', handleWindowMouseUp)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [
    chartData.length,
    clampTranslatedRange,
    commitAxisRanges,
    isLabelDragging,
    sensorDataExtents,
    singleScaleDataRange,
    uPlotRef,
  ])

  useEffect(() => {
    if (!enabled || !hasDataPoints) {
      return
    }

    let animationFrame = 0
    let cleanupAttachedListeners: (() => void) | undefined

    const attachListeners = () => {
      const uPlotInstance = uPlotRef.current
      const chartArea = chartAreaRef.current
      if (!uPlotInstance || !chartArea) {
        animationFrame = window.requestAnimationFrame(attachListeners)
        return
      }

      const handleNativeMouseDown = (event: MouseEvent) => {
        if (event.button === 0 && !event.shiftKey) {
          setSelectedAxisIds([])
        }

        if (event.button !== 2) {
          return
        }

        const plotBounds = getPlotBounds(uPlotInstance, chartArea)
        if (!plotBounds) {
          return
        }

        const chartRect = chartArea.getBoundingClientRect()
        const startX = Math.min(
          plotBounds.right,
          Math.max(plotBounds.left, event.clientX - chartRect.left)
        )
        const startY = Math.min(
          plotBounds.bottom,
          Math.max(plotBounds.top, event.clientY - chartRect.top)
        )

        event.preventDefault()
        setSelectedAxisIds([])
        setPreviewAxisRanges({})
        boxZoomSessionRef.current = {
          currentX: startX,
          currentY: startY,
          didMove: false,
          startX,
          startY,
        }
        setBoxZoomRect({
          left: startX,
          top: startY,
          width: 0,
          height: 0,
        })
        setIsBoxZoomDragging(true)
      }

      const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault()
      }

      uPlotInstance.over.addEventListener('mousedown', handleNativeMouseDown)
      uPlotInstance.over.addEventListener('contextmenu', handleContextMenu)

      cleanupAttachedListeners = () => {
        uPlotInstance.over.removeEventListener(
          'mousedown',
          handleNativeMouseDown
        )
        uPlotInstance.over.removeEventListener('contextmenu', handleContextMenu)
      }
    }

    attachListeners()

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame)
      }
      cleanupAttachedListeners?.()
    }
  }, [chartAreaRef, enabled, hasDataPoints, uPlotRecreateKey, uPlotRef])

  useEffect(() => {
    if (!isBoxZoomDragging) {
      return
    }

    const handleWindowMouseMove = (event: MouseEvent) => {
      const session = boxZoomSessionRef.current
      const uPlotInstance = uPlotRef.current
      const chartArea = chartAreaRef.current
      if (!session || !uPlotInstance || !chartArea) {
        return
      }

      const plotBounds = getPlotBounds(uPlotInstance, chartArea)
      if (!plotBounds) {
        return
      }

      const chartRect = chartArea.getBoundingClientRect()
      const nextX = Math.min(
        plotBounds.right,
        Math.max(plotBounds.left, event.clientX - chartRect.left)
      )
      const nextY = Math.min(
        plotBounds.bottom,
        Math.max(plotBounds.top, event.clientY - chartRect.top)
      )

      session.currentX = nextX
      session.currentY = nextY
      session.didMove =
        session.didMove ||
        Math.abs(nextX - session.startX) >= DRAG_THRESHOLD_PX ||
        Math.abs(nextY - session.startY) >= DRAG_THRESHOLD_PX

      setBoxZoomRect({
        left: Math.min(session.startX, nextX),
        top: Math.min(session.startY, nextY),
        width: Math.abs(nextX - session.startX),
        height: Math.abs(nextY - session.startY),
      })
    }

    const handleWindowMouseUp = () => {
      const session = boxZoomSessionRef.current
      const uPlotInstance = uPlotRef.current
      const chartArea = chartAreaRef.current
      if (session && session.didMove && uPlotInstance && chartArea) {
        const plotBounds = getPlotBounds(uPlotInstance, chartArea)

        if (plotBounds) {
          const left = Math.min(session.startX, session.currentX)
          const right = Math.max(session.startX, session.currentX)
          const top = Math.min(session.startY, session.currentY)
          const bottom = Math.max(session.startY, session.currentY)

          if (
            right - left >= DRAG_THRESHOLD_PX &&
            bottom - top >= DRAG_THRESHOLD_PX
          ) {
            const minX = uPlotInstance.posToVal(left - plotBounds.left, 'x')
            const maxX = uPlotInstance.posToVal(right - plotBounds.left, 'x')
            const nextAxisRanges = boxZoomAxisIds.reduce<
              Record<string, NumericAxisRange>
            >((ranges, axisId) => {
              const scaleKey = axisId === SINGLE_SCALE_AXIS_ID ? 'y' : axisId
              const topValue = uPlotInstance.posToVal(
                top - plotBounds.top,
                scaleKey
              )
              const bottomValue = uPlotInstance.posToVal(
                bottom - plotBounds.top,
                scaleKey
              )
              if (!Number.isFinite(topValue) || !Number.isFinite(bottomValue)) {
                return ranges
              }

              ranges[axisId] = {
                min: Math.min(topValue, bottomValue),
                max: Math.max(topValue, bottomValue),
              }
              return ranges
            }, {})

            applyAxisRangePreview(uPlotInstance, nextAxisRanges)
            setPreviewAxisRanges(cloneNumericRangeMap(nextAxisRanges))

            if (Number.isFinite(minX) && Number.isFinite(maxX)) {
              if (xAxisSensorId) {
                commitAxisRanges(nextAxisRanges)
                setCustomXAxisRange({
                  min: Math.min(minX, maxX),
                  max: Math.max(minX, maxX),
                })
              } else {
                // Clamp before committing. The previous `Math.max(1, ...)` only
                // sanitized the getInterval argument -- the raw, possibly
                // zero-width/future from/to were still committed. The floor is
                // derived from the cadence of the data on screen, so a box
                // narrower than a few real samples widens to something that can
                // actually be plotted instead of returning an empty chart.
                const clamped = clampTimeRange(minX, maxX, {
                  minSpanMs: minZoomSpanRef.current,
                })

                if (clamped) {
                  const rangeSeconds = (clamped.to - clamped.from) / 1000
                  const { interval, interval_unit } =
                    TimeSeriesService.getIntervalForPenCount(
                      rangeSeconds,
                      penCountRef.current
                    )
                  pendingBoxZoomAxisRangesRef.current = nextAxisRanges
                  setPreviewXAxisRange({
                    min: clamped.from,
                    max: clamped.to,
                  })

                  loadTriggerRef.current = 'zoom'
                  setDateMode('absolute')
                  setLiveTail(false)
                  setTimeRange({
                    from: dayjs(clamped.from).toISOString(),
                    to: dayjs(clamped.to).toISOString(),
                    preset: 'custom',
                    interval,
                    intervalUnit: interval_unit,
                  })
                }
              }
            }
          }
        }
      }

      boxZoomSessionRef.current = null
      setIsBoxZoomDragging(false)
      setBoxZoomRect(null)
    }

    window.addEventListener('mousemove', handleWindowMouseMove)
    window.addEventListener('mouseup', handleWindowMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove)
      window.removeEventListener('mouseup', handleWindowMouseUp)
    }
  }, [
    applyAxisRangePreview,
    boxZoomAxisIds,
    chartAreaRef,
    commitAxisRanges,
    isBoxZoomDragging,
    loadTriggerRef,
    setCustomXAxisRange,
    setDateMode,
    setLiveTail,
    setTimeRange,
    uPlotRef,
    xAxisSensorId,
  ])

  return {
    boxZoomRect,
    handleAxisLabelDoubleClick,
    handleAxisLabelMouseDown,
    handleAxisLabelMouseEnter: setHoveredAxisId,
    handleAxisLabelMouseLeave: () => setHoveredAxisId(null),
    hoveredAxisId,
    isLabelDragging,
    previewXAxisRange,
    previewAxisRanges,
    selectedAxisIds,
  }
}
